import {
  Activity,
  ArrowUpRight,
  CreditCard,
  Globe2,
  KeyRound,
  Link2,
  MessageSquare,
  Radio,
  RefreshCw,
  Server,
  ShieldCheck,
  Users,
} from "lucide-react";
import { EmptyState } from "../common/StateContainer";

const Metric = ({ accent, icon: Icon, label, onClick, testId, value }) => (
  <button
    type="button"
    onClick={onClick}
    className="group relative flex min-w-0 flex-col gap-4 rounded-xl border border-slate-800/70 bg-[#0F172A] px-4 py-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-700 hover:bg-[#131F38] hover:shadow-xl focus:outline-none wx-focusable"
    data-testid={testId}
  >
    <div className="flex items-center justify-between">
      <span className={`grid h-9 w-9 place-items-center rounded-lg border ${accent.ring} ${accent.bg}`}>
        <Icon className={`h-4 w-4 ${accent.icon}`} />
      </span>
      <ArrowUpRight className="h-3.5 w-3.5 text-slate-700 transition-colors group-hover:text-cyan-300" />
    </div>
    <div>
      <p className={`font-mono text-3xl font-bold tabular-nums ${accent.value}`}>
        {Number(value || 0).toLocaleString()}
      </p>
      <p className="wx-metric-label mt-1">{label}</p>
    </div>
  </button>
);

const Queue = ({ empty, items, onOpen, testId, title, total }) => (
  <section className="wx-card min-w-0 p-0 overflow-hidden" data-testid={testId}>
    <header className="flex items-center justify-between border-b border-slate-800/70 px-4 py-3">
      <div>
        <p className="wx-overline">{title}</p>
        <p className="mt-1 text-[11px] text-slate-500">Requires operator review</p>
      </div>
      <span className={`wx-chip ${total > 0 ? "wx-chip-amber" : "wx-chip-slate"}`}>{total} open</span>
    </header>
    {items.length ? (
      <div className="divide-y divide-slate-800/60">
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={onOpen}
            className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-900/50"
            data-testid={`${testId}-item-${item.id}`}
          >
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium text-slate-100">{item.title}</span>
              <span className="mt-1 block truncate text-xs text-slate-500">{item.meta}</span>
            </span>
            <ArrowUpRight className="h-4 w-4 text-slate-600" />
          </button>
        ))}
      </div>
    ) : (
      <div className="px-4 py-10">
        <EmptyState icon={ShieldCheck} title="All clear" description={empty} compact testId={`${testId}-empty`} />
      </div>
    )}
  </section>
);

export const AdminOverviewWorkspace = ({
  assignments,
  domains,
  maintenance,
  onNavigate,
  onRefresh,
  pendingTransactions,
  servers,
  stats,
  tickets,
}) => {
  const pending = pendingTransactions.filter((item) => item.status === "pending");
  const openTickets = tickets.filter((item) => item.status !== "closed" && item.status !== "resolved");
  const onlineServers = servers.filter((item) => item.is_online).length;

  const palettes = {
    slate:   { ring: "border-slate-700",      bg: "bg-slate-800/60",   icon: "text-slate-300",   value: "text-slate-100" },
    emerald: { ring: "border-emerald-500/25", bg: "bg-emerald-500/10", icon: "text-emerald-300", value: "text-emerald-300" },
    cyan:    { ring: "border-cyan-500/25",    bg: "bg-cyan-500/10",    icon: "text-cyan-300",    value: "text-cyan-300" },
    indigo:  { ring: "border-indigo-500/25",  bg: "bg-indigo-500/10",  icon: "text-indigo-300",  value: "text-indigo-300" },
    amber:   { ring: "border-amber-500/25",   bg: "bg-amber-500/10",   icon: "text-amber-300",   value: "text-amber-300" },
    rose:    { ring: "border-rose-500/25",    bg: "bg-rose-500/10",    icon: "text-rose-300",    value: "text-rose-300" },
  };

  const metrics = [
    { label: "Total users",      value: stats?.total_users,                                 icon: Users,         accent: palettes.slate,   tab: "users",        id: "overview-total-users" },
    { label: "Licensed",         value: stats?.licensed_users,                              icon: KeyRound,      accent: palettes.emerald, tab: "licenses",     id: "overview-licensed-users" },
    { label: "Domains",          value: stats?.total_domains || domains.length,             icon: Globe2,        accent: palettes.cyan,    tab: "domains",      id: "overview-total-domains" },
    { label: "Assignments",      value: stats?.total_assignments || assignments.length,     icon: Link2,         accent: palettes.indigo,  tab: "assignments",  id: "overview-total-assignments" },
    { label: "Pending payments", value: pending.length,                                     icon: CreditCard,    accent: palettes.amber,   tab: "transactions", id: "overview-pending-transactions" },
    { label: "Open tickets",     value: openTickets.length,                                 icon: MessageSquare, accent: palettes.rose,    tab: "support",      id: "overview-open-tickets" },
  ];

  return (
    <div className="mx-auto w-full max-w-[1800px] space-y-6" data-testid="admin-overview-section">
      {/* ===== Header ===== */}
      <header className="flex flex-col gap-5 border-b border-slate-800/80 pb-6 xl:flex-row xl:items-end xl:justify-between">
        <div className="space-y-3">
          <span className="wx-tactical" data-testid="admin-overview-overline">
            <Radio className="h-3 w-3" /> System HUD
          </span>
          <h1 className="font-orbitron text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl" data-testid="admin-overview-title">
            Platform Overview
          </h1>
          <p className="max-w-2xl text-sm text-slate-400">
            Live operational state, intervention queues, and fleet readiness.
          </p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          className="wx-btn-outline self-start xl:self-end"
          data-testid="admin-refresh-all"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh telemetry
        </button>
      </header>

      {/* ===== Maintenance banner ===== */}
      <section
        className={`grid gap-4 rounded-xl border p-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,520px)_auto] lg:items-center ${
          maintenance.enabled
            ? "border-rose-500/35 bg-rose-500/5"
            : "border-emerald-500/25 bg-emerald-500/5"
        }`}
        data-testid="maintenance-control-panel"
      >
        <div className="flex items-center gap-3">
          <span
            className={`grid h-10 w-10 shrink-0 place-items-center rounded-lg border ${
              maintenance.enabled ? "border-rose-500/40 bg-rose-500/15 text-rose-300" : "border-emerald-500/40 bg-emerald-500/15 text-emerald-300"
            }`}
          >
            <ShieldCheck className="h-5 w-5" />
          </span>
          <div>
            <p className="text-sm font-semibold text-slate-100" data-testid="maintenance-status">
              {maintenance.enabled ? "Maintenance mode active" : "Platform operational"}
            </p>
            <p className="mt-0.5 text-xs text-slate-500">Controls licensed-user access globally.</p>
          </div>
        </div>
        <input
          type="text"
          value={maintenance.message}
          onChange={(event) => maintenance.setMessage(event.target.value)}
          className="h-10 w-full rounded-lg border border-slate-800 bg-slate-950/60 px-3 text-sm text-slate-100 outline-none transition-colors placeholder:text-slate-600 focus:border-cyan-500/50"
          placeholder="Maintenance message"
          data-testid="maintenance-message-input"
        />
        <button
          type="button"
          onClick={maintenance.toggle}
          disabled={maintenance.updating}
          className={`wx-btn h-10 whitespace-nowrap text-sm ${
            maintenance.enabled
              ? "border border-emerald-500/35 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
              : "border border-rose-500/35 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20"
          }`}
          data-testid="maintenance-toggle-button"
        >
          {maintenance.updating ? "Updating…" : maintenance.enabled ? "Resume platform" : "Enter maintenance"}
        </button>
      </section>

      {/* ===== KPI Grid ===== */}
      <section
        className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6"
        data-testid="admin-kpi-grid"
      >
        {metrics.map((metric) => (
          <Metric
            key={metric.id}
            icon={metric.icon}
            label={metric.label}
            value={metric.value}
            accent={metric.accent}
            onClick={() => onNavigate(metric.tab)}
            testId={metric.id}
          />
        ))}
      </section>

      {/* ===== Queues + Fleet readiness ===== */}
      <div className="grid min-w-0 gap-5 xl:grid-cols-12">
        <div className="grid min-w-0 gap-5 md:grid-cols-2 xl:col-span-8">
          <Queue
            title="Payment verification"
            total={pending.length}
            items={pending.slice(0, 5).map((item) => ({
              id: item.id,
              title: item.username || "Unknown user",
              meta: `$${Number(item.amount || 0).toFixed(2)} · ${(item.wallet_type || "BTC").toUpperCase()}`,
            }))}
            empty="Payment queue is clear"
            onOpen={() => onNavigate("transactions")}
            testId="overview-payment-queue"
          />
          <Queue
            title="Support queue"
            total={openTickets.length}
            items={openTickets.slice(0, 5).map((item) => ({
              id: item.id,
              title: item.subject,
              meta: `${item.username} · ${item.status}`,
            }))}
            empty="Support queue is clear"
            onOpen={() => onNavigate("support")}
            testId="overview-support-queue"
          />
        </div>

        <section className="wx-card min-w-0 p-0 xl:col-span-4" data-testid="overview-fleet-readiness">
          <header className="flex items-center justify-between border-b border-slate-800/70 px-4 py-3">
            <div>
              <p className="wx-overline">Fleet readiness</p>
              <p className="mt-1 text-[11px] text-slate-500">Redirect infrastructure snapshot</p>
            </div>
            <span
              className={`wx-chip ${onlineServers === servers.length && servers.length ? "wx-chip-emerald" : onlineServers === 0 ? "wx-chip-rose" : "wx-chip-amber"}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${onlineServers === servers.length && servers.length ? "bg-emerald-400 animate-pulse" : "bg-amber-400"}`} />
              {servers.length ? "Live" : "Idle"}
            </span>
          </header>
          <div className="divide-y divide-slate-800/60">
            <button
              type="button"
              onClick={() => onNavigate("servers")}
              className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-slate-900/50"
              data-testid="overview-fleet-servers"
            >
              <span className="flex items-center gap-2 text-sm text-slate-200">
                <Server className="h-4 w-4 text-emerald-400" />
                Agents online
              </span>
              <span className="font-mono text-sm font-semibold text-emerald-300 tabular-nums">
                {onlineServers}/{servers.length}
              </span>
            </button>
            <button
              type="button"
              onClick={() => onNavigate("domains")}
              className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-slate-900/50"
              data-testid="overview-domains-awaiting"
            >
              <span className="flex items-center gap-2 text-sm text-slate-200">
                <Globe2 className="h-4 w-4 text-amber-400" />
                Awaiting assignment
              </span>
              <span className="font-mono text-sm font-semibold text-amber-300 tabular-nums">
                {stats?.domains_awaiting_assignment || 0}
              </span>
            </button>
            <button
              type="button"
              onClick={() => onNavigate("config-sync")}
              className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-slate-900/50"
              data-testid="overview-config-sync"
            >
              <span className="flex items-center gap-2 text-sm text-slate-200">
                <Activity className="h-4 w-4 text-cyan-400" />
                Config delivery
              </span>
              <span className="text-xs text-cyan-300 underline-offset-2 group-hover:underline">
                Inspect sync →
              </span>
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};
